/* eslint-disable no-console */
import { useGetContentDeepLinkMutation } from 'app/data/mutations/use-get-content-deep-link-query';
import { useContent } from 'app/shared/contexts/content';
import { useCallback, useEffect, useRef, useState } from 'react';

const SHARE_ERROR_MESSAGE = 'Share canceled';

interface UseShareValues {
  share: () => Promise<void>;
  showCopiedText: boolean;
}
export const useShare = (): UseShareValues => {
  const { content } = useContent();
  const title = content?.title;
  const contentUuid = content?.uuid;
  const { mutateAsync: getContentDeepLink, isReady: isDeepLinkReady } = useGetContentDeepLinkMutation();
  const [deeplinkData, setDeeplinkData] = useState<{ url: string } | null>(null);
  const [showCopiedText, setShowCopiedText] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopyToClipboard = useCallback(async (url?: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      setShowCopiedText(false);
      timeoutRef.current = null;
      return;
    }

    try {
      // Prefer the url resolved by the caller (share() may have just fetched it when the
      // preload hadn't populated state yet); fall back to preloaded state, then empty.
      await navigator.clipboard.writeText(url ?? deeplinkData?.url ?? '');

      setShowCopiedText(true);
      timeoutRef.current = setTimeout(() => {
        setShowCopiedText(false);
        timeoutRef.current = null;
      }, 1500);
    } catch (writeTextError) {
      console.warn('Error copying to clipboard:', writeTextError);
    }
  }, [deeplinkData?.url]);

  useEffect(() => {
    if (contentUuid && isDeepLinkReady) {
      getContentDeepLink()
        .then((deeplink) => setDeeplinkData(deeplink))
        .catch((error) => console.warn(error));
    }
  }, [contentUuid, isDeepLinkReady, getContentDeepLink]);

  useEffect(() => () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  const share = useCallback(async () => {
    const deeplink = deeplinkData || await getContentDeepLink();
    const deeplinkUrl = deeplink?.url;
    const shareData = {
      title: title ?? deeplinkUrl,
      url: deeplinkUrl,
    };

    try {
      if (navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        await handleCopyToClipboard(deeplinkUrl);
      }
    } catch (shareError) {
      if (shareError instanceof Error && shareError.message.includes(SHARE_ERROR_MESSAGE)) {
        console.log('User canceled sharing:', shareError.message);
      } else {
        await handleCopyToClipboard(deeplinkUrl);
        console.log('Sharing is unsupported on this device, copying to clipboard', shareError);
      }
    }
  }, [deeplinkData, getContentDeepLink, title, handleCopyToClipboard]);

   return { share, showCopiedText };
};
